// Notes for one lift, or for every lift in a workout plus that workout's past notes (Kenny, 2026-11-03).
// Every note is dated, can be edited or deleted, and comes back every time the lift does.
import { loadLiftNotes, addLiftNote, updateLiftNote, deleteLiftNote, loadSessions, updateSessionNote } from '../lib/grit-data.js';
import { notesByLift, workoutNotesFor, noteDate } from '../lib/lift-notes.js';
import { displayName, escapeHtml } from '../lib/exercise-names.js';
import { confirmBox } from './dialog.js';
import { toast } from './toast.js';
import { icon } from './icons.js';

// lifts: [{ exercise_id, exercise }]. dayId: also show this workout day's past workout notes.
// date: the day a new note is about (the workout's date). onChange runs after any save.
export async function openNotes({ title, lifts = [], dayId = null, date, readOnly = false, onChange }) {
  const single = lifts.length === 1;
  let byLift = {};
  let workoutNotes = [];
  let editing = null;   // { kind: 'lift' | 'workout', id }
  let adding = single ? lifts[0].exercise_id : null;

  const overlay = document.createElement('div');
  overlay.className = 'fixed inset-0 z-[130] flex items-end justify-center bg-black/30';
  overlay.innerHTML = '<div class="grit-sheet overflow-auto"><div class="grit-sheet-handle"></div><p class="text-inksoft py-6 text-center">Loading…</p></div>';
  document.body.appendChild(overlay);
  const close = () => overlay.remove();
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  async function load() {
    const [rows, sessions] = await Promise.all([loadLiftNotes(), dayId ? loadSessions() : Promise.resolve([])]);
    byLift = notesByLift(rows);
    workoutNotes = dayId ? workoutNotesFor(sessions, dayId) : [];
  }

  const btn = (attrs, label, extra = '') => `<button ${attrs} class="min-h-[44px] px-2 text-[15px] font-semibold ${extra}">${label}</button>`;

  function noteRow(kind, n, when, text) {
    if (editing?.kind === kind && editing.id === n.id) {
      return `<div class="bg-pagebg rounded-2xl p-3">
        <div class="grit-eyebrow">${when}</div>
        <textarea id="edit-box" rows="3" maxlength="500" class="grit-input mt-1.5 !py-2.5 resize-none" aria-label="Edit note">${escapeHtml(text)}</textarea>
        <div class="flex justify-end gap-1 mt-1">${btn('data-cancel-edit', 'Cancel', 'text-inksoft')}${btn('data-save-edit', 'Save', 'text-grit-press')}</div>
      </div>`;
    }
    const written = (n.date || n.created_at || '').slice(0, 10);
    const edited = !n.updated_at ? '' : n.updated_at.slice(0, 10) === written ? ' · edited' : ` · edited ${noteDate(n.updated_at)}`;
    return `<div class="bg-pagebg rounded-2xl p-3">
      <div class="grit-eyebrow">${when}${edited}</div>
      <div class="text-[15px] text-ink mt-1 whitespace-pre-line">${escapeHtml(text)}</div>
      ${readOnly ? '' : `<div class="flex justify-end gap-1 -mb-2">${btn(`data-edit="${kind}:${n.id}"`, 'Edit', 'text-grit-press')}${btn(`data-del="${kind}:${n.id}"`, 'Delete', 'text-danger')}</div>`}
    </div>`;
  }

  function liftSection(l) {
    const list = byLift[l.exercise_id] || [];
    const name = escapeHtml(displayName(l.exercise) || l.exercise?.name || 'Lift');
    const addBox = adding === l.exercise_id && !readOnly ? `<div class="mt-2">
        <textarea id="add-box" rows="2" maxlength="500" placeholder="Stay at 225 until form is clean" class="grit-input !py-2.5 resize-none" aria-label="New note for ${name}"></textarea>
        <button id="add-save" class="grit-btn mt-2">Add note</button>
      </div>` : '';
    return `<section class="${single ? '' : 'mt-5'}">
      ${single ? '' : `<div class="flex items-center justify-between gap-2"><div class="font-bold text-[17px] text-ink min-w-0">${name}</div>${readOnly || adding === l.exercise_id ? '' : btn(`data-add="${l.exercise_id}"`, 'Add', 'text-grit-press flex-none')}</div>`}
      ${addBox}
      <div class="space-y-2 mt-2">${list.length ? list.map((n) => noteRow('lift', n, noteDate(n.date || n.created_at), n.body)).join('') : `<div class="text-[15px] text-inksoft">No notes yet.</div>`}</div>
    </section>`;
  }

  function paint() {
    const sheet = overlay.querySelector('.grit-sheet');
    const keep = sheet ? sheet.scrollTop : 0;
    overlay.innerHTML = `<div class="grit-sheet overflow-auto">
      <div class="grit-sheet-handle"></div>
      <div class="flex items-start justify-between gap-2 mb-1">
        <div class="min-w-0"><div class="text-[20px] font-bold tracking-tight text-ink">Notes</div><div class="text-[15px] text-inksoft truncate">${escapeHtml(title || '')}</div></div>
        <button data-close aria-label="Close" class="text-inksoft w-11 h-11 -mr-2 flex-none flex items-center justify-center">${icon.x('w-5 h-5')}</button>
      </div>
      ${single ? '' : '<div class="text-[13px] text-inksoft">Each note stays with its lift and shows up every time you do it.</div>'}
      ${lifts.map(liftSection).join('')}
      ${dayId ? `<section class="mt-6"><div class="font-bold text-[17px] text-ink">Workout notes</div>
        <div class="space-y-2 mt-2">${workoutNotes.length ? workoutNotes.map((s) => noteRow('workout', s, noteDate(s.date), s.notes)).join('') : '<div class="text-[15px] text-inksoft">None yet. Add one with "Today\'s note" at the end of a workout.</div>'}</div></section>` : ''}
      <div class="h-6"></div>
    </div>`;
    overlay.querySelector('.grit-sheet').scrollTop = keep;
    bind();
  }

  function find(kind, id) { return kind === 'workout' ? workoutNotes.find((s) => s.id === id) : Object.values(byLift).flat().find((n) => n.id === id); }

  function bind() {
    overlay.querySelector('[data-close]').onclick = close;
    overlay.querySelectorAll('[data-add]').forEach((b) => { b.onclick = () => { adding = b.dataset.add; editing = null; paint(); overlay.querySelector('#add-box')?.focus(); }; });
    const save = overlay.querySelector('#add-save');
    if (save) save.onclick = async () => {
      const text = overlay.querySelector('#add-box').value.trim();
      if (!text) { overlay.querySelector('#add-box').focus(); return; }
      save.disabled = true;
      if (!(await addLiftNote(adding, text, date))) { save.disabled = false; toast("Note didn't save, try again", { tone: 'bad' }); return; }
      if (!single) adding = null;
      await load(); paint(); onChange?.(); toast('Note saved');
    };
    overlay.querySelectorAll('[data-edit]').forEach((b) => { b.onclick = () => { const [kind, id] = b.dataset.edit.split(':'); editing = { kind, id }; paint(); overlay.querySelector('#edit-box')?.focus(); }; });
    const cancel = overlay.querySelector('[data-cancel-edit]');
    if (cancel) cancel.onclick = () => { editing = null; paint(); };
    const saveEdit = overlay.querySelector('[data-save-edit]');
    if (saveEdit) saveEdit.onclick = async () => {
      const text = overlay.querySelector('#edit-box').value.trim();
      if (!text) { overlay.querySelector('#edit-box').focus(); return; }
      const ok = editing.kind === 'workout' ? await updateSessionNote(editing.id, text) : await updateLiftNote(editing.id, text);
      if (!ok) { toast("Didn't save, try again", { tone: 'bad' }); return; }
      editing = null; await load(); paint(); onChange?.(); toast('Saved');
    };
    overlay.querySelectorAll('[data-del]').forEach((b) => {
      b.onclick = async () => {
        const [kind, id] = b.dataset.del.split(':');
        const n = find(kind, id);
        if (!(await confirmBox({ title: 'Delete this note?', message: `${noteDate(kind === 'workout' ? n.date : (n.date || n.created_at))}: ${(kind === 'workout' ? n.notes : n.body).slice(0, 80)}`, confirmLabel: 'Delete', danger: true }))) return;
        const ok = kind === 'workout' ? await updateSessionNote(id, null) : await deleteLiftNote(id);
        if (!ok) { toast("Couldn't delete it, try again", { tone: 'bad' }); return; }
        await load(); paint(); onChange?.(); toast('Deleted');
      };
    });
  }

  await load();
  paint();
}
